import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../api/axiosInstance';

const DEFAULT_APP_CONFIG = {
  tenant: 'saturn',
  appName: 'QuickFox',
  displayName: 'Saturn',
  publicUrl: window.location.origin,
  logoUrl: '/images/logo_quickfox_nobg.png',
  company: {
    name: 'SATURN LIS CERAMIKA SP.J.',
    logoUrl: '/images/company_saturn.png',
    addressLine1: 'Piotrkowska 199',
    postalCode: '26-300',
    city: 'Opoczno',
    taxId: '7681840666',
  },
  links: {
    productSearchUrlTemplate: 'https://phsaturn.pl/p?szukane={query}',
  },
  features: {
    nexoProductCustomFields: true,
    productSearchLink: true,
    patternSizeCompatibility: true,
    documentCostSummary: true,
    tradeDocuments: false,
    tradeZk: false,
    tradeOffer: false,
    tradeDocumentEditing: false,
    patternImageAutoRotation: false,
  },
  tradeDocuments: {
    defaultWarehouse: null,
    enabledByDatabase: true,
    editingEnabled: false,
    editingEnabledByDatabase: false,
    availableByDeployment: false,
  },
};

const AppConfigContext = createContext({
  appConfig: DEFAULT_APP_CONFIG,
  loading: true,
  refreshAppConfig: async () => DEFAULT_APP_CONFIG,
});

export function AppConfigProvider({ children }) {
  const [appConfig, setAppConfig] = useState(DEFAULT_APP_CONFIG);
  const [loading, setLoading] = useState(true);

  const mergeAppConfig = useCallback((data) => {
    if (!data) return;

    setAppConfig((current) => ({
      ...current,
      ...data,
      company: { ...current.company, ...data.company },
      links: { ...current.links, ...data.links },
      features: { ...current.features, ...data.features },
      tradeDocuments: { ...current.tradeDocuments, ...data.tradeDocuments },
    }));
  }, []);

  const refreshAppConfig = useCallback(async () => {
    const { data } = await axiosInstance.get('/app-config');
    mergeAppConfig(data);
    return data;
  }, [mergeAppConfig]);

  useEffect(() => {
    let active = true;

    axiosInstance.get('/app-config')
      .then(({ data }) => {
        if (active && data) {
          mergeAppConfig(data);
        }
      })
      .catch((error) => {
        console.warn('Nie udało się pobrać konfiguracji aplikacji. Użyto ustawień domyślnych.', error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [mergeAppConfig]);

  useEffect(() => {
    document.title = appConfig.displayName
      ? `${appConfig.appName} - ${appConfig.displayName}`
      : appConfig.appName;
  }, [appConfig.appName, appConfig.displayName]);

  const value = useMemo(
    () => ({ appConfig, loading, refreshAppConfig }),
    [appConfig, loading, refreshAppConfig]
  );

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  return useContext(AppConfigContext);
}

export { DEFAULT_APP_CONFIG };
